import {
  FileText,
  FileArchive,
  FileAudio,
  FileVideo,
  File,
} from 'lucide-react';
import {
  FaFilePdf,
  FaFileWord,
  FaFileExcel,
  FaFileImage,
  FaEnvelope,
  FaFileCsv,
} from 'react-icons/fa';

export const formatBytes = (bytes?: number | null): string => {
  if (bytes === undefined || bytes === null || isNaN(bytes)) return '';
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const dm = 2;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

// MSG and EML content types both count as email.
const EMAIL_CONTENT_TYPE_MARKERS = ['msg', 'outlook', 'rfc822', 'eml'];

export const isEmailContentType = (contentType: string): boolean => {
  if (!contentType) return false;
  const type = contentType.toLowerCase();
  return EMAIL_CONTENT_TYPE_MARKERS.some((marker) => type.includes(marker));
};

export const getFileTypeFilterKey = (contentType: string): string =>
  isEmailContentType(contentType) ? 'email' : contentType;

export const getFileTypeLabel = (contentType: string): string => {
  if (!contentType) return 'Unknown';
  if (contentType === 'email') return 'Email';
  const type = contentType.toLowerCase();
  if (type.includes('pdf')) return 'PDF';
  if (type.includes('msword') || type.includes('wordprocessingml')) return 'Word Document';
  if (type.includes('spreadsheetml') || type.includes('ms-excel')) return 'Excel Spreadsheet';
  if (type.includes('csv')) return 'CSV';
  if (type.includes('png')) return 'PNG Image';
  if (type.includes('jpeg') || type.includes('jpg')) return 'JPEG Image';
  if (type.includes('tiff')) return 'TIFF Image';
  if (type.includes('webp')) return 'WebP Image';
  if (type.includes('image')) return 'Image';
  if (type.includes('presentationml') || type.includes('powerpoint')) return 'Presentation';
  if (type.includes('zip')) return 'ZIP Archive';
  if (type.includes('tar') || type.includes('rar') || type.includes('archive')) return 'Archive';
  if (type.includes('audio')) return 'Audio';
  if (type.includes('video')) return 'Video';
  if (type.includes('text/plain')) return 'Plain Text';
  if (type.includes('text/html')) return 'HTML';
  if (type.includes('json')) return 'JSON';
  if (type.includes('xml')) return 'XML';
  return contentType;
};

export const getFileIcon = (contentType: string, className = 'w-8 h-8') => {
  if (!contentType) return <FileText className={`${className} text-emerald-400`} />;
  const type = contentType.toLowerCase();

  if (type.includes('pdf')) return <FaFilePdf className={`${className} text-red-500`} />;
  if (type.includes('spreadsheetml') || type.includes('ms-excel')) return <FaFileExcel className={`${className} text-green-600`} />;
  if (type.includes('doc')) return <FaFileWord className={`${className} text-blue-500`} />;
  if (type.includes('csv')) return <FaFileCsv className={`${className} text-emerald-500`} />;
  if (isEmailContentType(contentType))
    return <FaEnvelope className={`${className} text-sky-500`} />;
  if (type.includes('image') || type.includes('jpg') || type.includes('tiff') || type.includes('png') || type.includes('jpeg'))
    return <FaFileImage className={`${className} text-purple-400`} />;

  if (type.includes('presentation') || type.includes('powerpoint') || type.includes('ppt'))
    return <FileText className={`${className} text-orange-400`} />;
  if (type.includes('zip') || type.includes('tar') || type.includes('rar') || type.includes('archive'))
    return <FileArchive className={`${className} text-amber-400`} />;
  if (type.includes('audio')) return <FileAudio className={`${className} text-purple-400`} />;
  if (type.includes('video')) return <FileVideo className={`${className} text-pink-400`} />;
  return <File className={`${className} text-gray-400`} />;
};

const ISO_DURATION_REGEX = /^P(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)D)?$/;

export const formatRetentionPeriod = (iso: string | undefined | null): string => {
  if (!iso) return 'Unknown';
  const match = ISO_DURATION_REGEX.exec(iso);
  if (!match) return iso;
  const parts: string[] = [];
  if (match[1]) parts.push(`${match[1]} year${match[1] === '1' ? '' : 's'}`);
  if (match[2]) parts.push(`${match[2]} month${match[2] === '1' ? '' : 's'}`);
  if (match[3]) parts.push(`${match[3]} day${match[3] === '1' ? '' : 's'}`);
  return parts.join(', ') || iso;
};
